import { useEffect, useEffectEvent, useRef, useState } from 'react';
import { ApiError } from '@/api/errors';
import {
  commissionPlansApi,
  type CommissionPlanPreviewBody,
  type CommissionPlanWrite,
  type PlanPreview,
} from '@/api/commissionPlans';
import { isCardGone } from './exampleState';

/** How long the draft must stay still before tmm-api is asked for its preview. */
const PREVIEW_DEBOUNCE_MS = 400;

export interface PlanPreviewState {
  status: 'idle' | 'loading' | 'ready' | 'error';
  preview: PlanPreview | null;
  error: ApiError | null;
}

function toApiError(err: unknown): ApiError {
  return err instanceof ApiError ? err : new ApiError('UNKNOWN', 0, {});
}

/** The write body's keys (no `version`) plus the card: a key the form left out stays out (TMMAPI-0167). */
function previewBody(body: CommissionPlanWrite, rateCardId: number): CommissionPlanPreviewBody {
  const { version: _version, ...terms } = body;
  return { ...terms, rateCardId };
}

/**
 * tmm-api's commission-plan dry run (`POST /commission-plans/preview`, TMMAPI-0104): the limit
 * check and the 24-month example of a plan's values on one of the reseller's rate cards, computed by
 * the server so the console computes no commission. Debounced; only the latest request's answer
 * lands, and the last preview stays in place while the next one loads. A `null` body (a form with
 * client errors) or no rate card is idle: no request, the last preview kept. `onCardGone` runs when
 * an answer says the card is gone (`404 RATE_CARD_NOT_FOUND`, or `fields.rateCardId`).
 */
export function usePlanPreview(
  body: CommissionPlanWrite | null,
  rateCardId: number | null,
  onCardGone?: () => void,
): PlanPreviewState {
  const key =
    body === null || rateCardId === null ? null : JSON.stringify(previewBody(body, rateCardId));
  // A request starts out loading, so the example never shows its idle copy before the first one.
  const [state, setState] = useState<PlanPreviewState>(() => ({
    status: key === null ? 'idle' : 'loading',
    preview: null,
    error: null,
  }));
  const seq = useRef(0);
  const cardGone = useEffectEvent(() => onCardGone?.());

  useEffect(() => {
    seq.current += 1;
    const mine = seq.current;
    if (key === null) {
      setState((prev) => ({ status: 'idle', preview: prev.preview, error: null }));
      return undefined;
    }
    setState((prev) => ({ ...prev, status: 'loading', error: null }));
    const timer = setTimeout(() => {
      commissionPlansApi.previewPlan(JSON.parse(key) as CommissionPlanPreviewBody).then(
        (preview) => {
          if (seq.current !== mine) return;
          setState({ status: 'ready', preview, error: null });
          if (isCardGone({ status: 'ready', preview, error: null })) cardGone();
        },
        (err: unknown) => {
          if (seq.current !== mine) return;
          const error = toApiError(err);
          setState({ status: 'error', preview: null, error });
          if (isCardGone({ status: 'error', preview: null, error })) cardGone();
        },
      );
    }, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [key]);

  return state;
}
